import { FC, useCallback, useLayoutEffect } from 'react';
import { throttle } from 'lodash';
import { buildPostMediaName } from '../../../../structuredData/builders/buildPostMediaJsonLd';
import { buildImageSource, buildImageSourceSet } from '../../../../utils/buildImageSource';
import { useIsMobile } from '../../../../utils/getIsMobile';
import { useDoubleClick } from '../../../../utils/useDoubleClick';
import { Carousel } from '../../../carousel/Carousel';
import { CarouselDotsIndicators } from '../../../carousel/CarouselDotsIndicators/CarouselDotsIndicators';
import { Box } from '../../../layout/Box';
import { DataTestId } from '../../../layout/DataTestIdWrapper';
import { Image } from '../../../media/Image';
import { Retry } from '../../../media/Retry';
import { Video } from '../../../media/Video';
import { PostUI, ReactionState } from '../../types';
import { isPostStatusValidForReaction } from '../../utils/isPostStatusValidForReaction';
import { PostDetailsCardProps, usePostDetailsCardContext } from '../PostDetailsCard';
import { MediaContentClickBlocker } from '../styles';
import { getCarouselSizes, makeMediaParams } from '../utils';

interface PostCarouselProps {
    post: PostUI;
    index: PostDetailsCardProps['index'];
    resizeDisabled?: boolean;
    onVideoMuteChange: PostDetailsCardProps['onVideoMuteChange'];
    onVideoEnd: PostDetailsCardProps['onVideoEnd'];
    onVideoStart: PostDetailsCardProps['onVideoStart'];
    onVideoPlay: PostDetailsCardProps['onVideoPlay'];
    onVideoPause: PostDetailsCardProps['onVideoPause'];
    onSlideChange: PostDetailsCardProps['onSlideChange'];
    onReaction: PostDetailsCardProps['onReaction'];
}

export const PostCarousel: FC<PostCarouselProps> = ({
    post,
    index,
    resizeDisabled,
    onVideoMuteChange,
    onVideoEnd,
    onVideoStart,
    onVideoPlay,
    onVideoPause,
    onSlideChange,
    onReaction,
}) => {
    const {
        soundControlContainer,
        isProductExpanded,
        activeIndex,
        sizes,
        setIsProductExpanded,
        setActiveIndex,
        setSizes,
    } = usePostDetailsCardContext();
    const isMobile = useIsMobile();
    const canReact = isPostStatusValidForReaction(post.status);
    const mediaName = buildPostMediaName(post);

    const handleSlideChange = useCallback(
        (index: number) => {
            setActiveIndex(index);
            onSlideChange?.(makeMediaParams(post, index));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [onSlideChange, setActiveIndex],
    );

    const handleVideoMuteChange = useCallback(
        (mute: boolean) => onVideoMuteChange?.({ ...makeMediaParams(post, activeIndex), mute }),
        [activeIndex, post, onVideoMuteChange],
    );
    const handleVideoEnd = useCallback(
        () => onVideoEnd?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, onVideoEnd],
    );
    const handleVideoStart = useCallback(
        () => onVideoStart?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, onVideoStart],
    );
    const handleVideoPlay = useCallback(
        () => onVideoPlay?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, onVideoPlay],
    );
    const handleVideoPause = useCallback(
        () => onVideoPause?.(makeMediaParams(post, activeIndex)),
        [activeIndex, post, onVideoPause],
    );

    const handleDoubleClick = useDoubleClick(() => {
        onReaction({ postNumber: index, reactionState: ReactionState.LIKE });
    }, [index]);

    useLayoutEffect(() => {
        if (resizeDisabled) {
            return;
        }

        const resizeCallback = () => {
            const newSizes = getCarouselSizes(post);
            setSizes(newSizes);
        };

        window.addEventListener('resize', throttle(resizeCallback, 50));

        return () => {
            window.removeEventListener('resize', resizeCallback);
        };
    }, [setSizes, post, resizeDisabled]);

    if (!sizes.height || !sizes.width) {
        return null;
    }

    return (
        <Box position="relative">
            <Carousel
                width={sizes.width}
                height={sizes.height}
                maxWidth={{ sm: '100%' }}
                count={post.content.length}
                activeIndex={activeIndex}
                onSlideChange={handleSlideChange}
                onClick={canReact ? handleDoubleClick : undefined}
                dataTestId={DataTestId.POST_CAROUSEL}
                renderItem={(index) => {
                    const slide = post.content[index];

                    return (
                        <Box
                            key={index}
                            width={sizes.width}
                            height={sizes.height}
                            maxWidth={{ sm: '100%' }}
                            position="relative"
                            overflowY="hidden"
                        >
                            {slide.type === 'image' && (
                                <Retry>
                                    <Image
                                        alt={mediaName}
                                        src={buildImageSource(slide.media, 'card')}
                                        srcSet={buildImageSourceSet(slide.media, 'card')}
                                        width={sizes.width}
                                        height={sizes.height}
                                        maxWidth={{ sm: '100%' }}
                                    />
                                </Retry>
                            )}
                            {slide.type === 'video' && (
                                <Video
                                    media={slide.media[0]}
                                    title={mediaName}
                                    onVideoMuteChange={handleVideoMuteChange}
                                    onVideoEnd={handleVideoEnd}
                                    onVideoStart={handleVideoStart}
                                    onVideoPlay={handleVideoPlay}
                                    onVideoPause={handleVideoPause}
                                    soundContainer={
                                        index === activeIndex ? soundControlContainer : undefined
                                    }
                                />
                            )}
                            {isProductExpanded ? (
                                <MediaContentClickBlocker
                                    width={sizes.width}
                                    height={sizes.height}
                                    maxWidth={{ sm: '100%' }}
                                    onClick={() => setIsProductExpanded(false)}
                                />
                            ) : null}
                            {isMobile ? (
                                <>
                                    <Box
                                        position="absolute"
                                        top={0}
                                        left={0}
                                        width="100%"
                                        height={88}
                                        background="linear-gradient(180deg, rgba(0, 0, 0, 0.3) 0%, rgba(0, 0, 0, 0) 100%)"
                                    />
                                    <Box
                                        position="absolute"
                                        bottom={0}
                                        left={0}
                                        width="100%"
                                        height={168}
                                        background="linear-gradient(180deg, rgba(0, 0, 0, 0) 30%, rgba(0, 0, 0, 0.3) 80%)"
                                    />
                                </>
                            ) : null}
                        </Box>
                    );
                }}
            />
            {isMobile ? null : (
                <Box
                    position="absolute"
                    bottom="8px"
                    left="50%"
                    transform="translateX(-50%)"
                    zIndex={3}
                >
                    <CarouselDotsIndicators
                        slidesCount={post.content.length}
                        activeIndex={activeIndex}
                    />
                </Box>
            )}
        </Box>
    );
};
